/**
 * AvailableBalanceCard — renders the patient's balance lines, one tile per
 * `subject`. Sits at the top of the Patient Billing tab (PROD-733).
 *
 * Flutter ref: `patient_available_balance_widget.dart` +
 * `scrips_core/lib/widgets/general/balance_indicator_widget.dart`.
 *
 * Empty / loading / error states all render a stable footprint so the
 * tab body doesn't reflow when the query settles.
 *
 * ## PROD-1761 — three defects fixed here
 *
 * 1. The card was titled "Available balance" while the wire returns two
 *    top-level totals, one of which IS "Available Balance" — so the container
 *    was named after one of its own children. The original comment above said
 *    `subject` was "Cash" / "Insurance", which is what made the single title
 *    reasonable when this was written; the wire does not do that.
 * 2. `financialEventStatus` ("negative" / "positive") was rendered to users
 *    verbatim as a third line of the tile. Flutter never displays it — it is a
 *    style input only (`patient_available_balance_widget.dart:36` passes
 *    `description: ''`). There is no human-readable label to copy from Flutter,
 *    so the parity-correct fix is removal, not relabelling.
 * 3. Money owed carried no visual signal — the amount rendered in the same
 *    brand blue as a credit. Flutter draws a 2px RED left accent for a negative
 *    balance and green for positive
 *    (`patient_available_balance_widget.dart:38` → `verticalLine(width: 2)` at
 *    `balance_indicator_widget.dart:41`).
 *
 * Money uses the shared `formatMoney` (`@/shared/lib/money`) — ungrouped
 * `.toFixed(2)`, matching Flutter's `toStringAsFixed(2)`. This file was where
 * React's earlier self-disagreement was diagnosed (billing used `toLocaleString`,
 * patients used `.toFixed(2)`) and the fix deferred to its own ticket; PROD-1804
 * shipped that shared formatter and this now consumes it. Behaviour is unchanged
 * — `formatMoney` is `.toFixed(2)`.
 */

import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientAvailableBalance } from '@/patients/services/use-patient-available-balance';
import type { BalanceLine } from '@/shared/types/billing';

import { formatMoney } from '@/shared/lib/money';
import { V2_CARD_EDGE, V2_EYEBROW, v2Look } from '@/shared/ui/design-v2';
/**
 * Flutter `Financial_Status_Positive` (`app_constants.dart:730`).
 *
 * Note the polarity: Flutter tests `== positive ? green : red`, so anything
 * unrecognised — including a null status or the `"Available"` / `"On Hold"`
 * values that `shared/types/billing.ts` documents — renders as owed. Defaulting
 * an unknown balance to "credit" would be the unsafe direction.
 */
const FINANCIAL_STATUS_POSITIVE = 'positive';

function isPositiveBalance(status: string | null | undefined): boolean {
  return status?.trim().toLowerCase() === FINANCIAL_STATUS_POSITIVE;
}

export interface AvailableBalanceCardProps {
  patientId: string | null | undefined;
}

export function AvailableBalanceCard({ patientId }: AvailableBalanceCardProps) {
  const { data, isLoading, isError } = usePatientAvailableBalance(patientId);
  const lines = (data?.balance ?? []).filter(
    (b): b is BalanceLine => b != null,
  );

  if (isLoading) {
    return (
      <div className={v2Look('rounded-lg border border-border-default bg-surface-card p-4', V2_CARD_EDGE)}>
        <div className={v2Look('text-xs font-bold text-text-muted uppercase tracking-wide mb-3', V2_EYEBROW)}>
          Balances
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-14 w-32" />
          <Skeleton className="h-14 w-32" />
        </div>
      </div>
    );
  }

  if (isError || !patientId) {
    return (
      <div className="rounded-lg border border-dashed border-border-default p-4 text-center">
        <p className="text-sm text-text-muted">
          {isError
            ? 'Could not load available balance.'
            : 'Save the patient first to see their available balance.'}
        </p>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className={v2Look('rounded-lg border border-border-default bg-surface-card p-4', V2_CARD_EDGE)}>
        <div className={v2Look('text-xs font-bold text-text-muted uppercase tracking-wide mb-1', V2_EYEBROW)}>
          Balances
        </div>
        <p className="text-sm text-text-muted">
          No advance balance on file.
        </p>
      </div>
    );
  }

  return (
    <div data-slot="billing-balances" className={v2Look('rounded-lg border border-border-default bg-surface-card p-4', V2_CARD_EDGE)}>
      <div className={v2Look('text-xs font-bold text-text-muted uppercase tracking-wide mb-3', V2_EYEBROW)}>
        Balances
      </div>
      <div className="flex flex-wrap gap-3">
        {lines.map((line, i) => {
          const isPositive = isPositiveBalance(line.financialEventStatus);
          return (
            <div
              key={`${line.subject ?? 'unknown'}-${i}`}
              className="min-w-35 rounded-md border border-border-default bg-blue-100/40 px-3 py-2"
            >
              <div className="text-xs text-text-muted">
                {sentenceCaseSubject(line.subject ?? 'Unknown')}
              </div>
              {/*
                Text colour must come from the explicit ramp, not the semantic
                token. `text-status-error` aliases red/500, the same shade as the
                accent fill, and goes invisible on this `bg-blue-100/40` tint —
                see the three-shade rule in CLAUDE.md (bg /100, fill /500,
                text /700). A credit keeps brand-depth, which is blue-700 and so
                already satisfies the rule against the blue tint.
              */}
              <div
                className={[
                  'text-base font-semibold',
                  isPositive
                    ? 'text-brand-depth'
                    : 'text-red-700',
                ].join(' ')}
              >
                AED {formatMoney(line.amount ?? 0)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** "Total Unpaid" -> "Total unpaid": plain Title Case words only; acronyms and mixed case stay as sent. */
function sentenceCaseSubject(subject: string): string {
  return /^[A-Z][a-z]+( [A-Z][a-z]+)+$/.test(subject) ? subject.charAt(0) + subject.slice(1).toLowerCase() : subject;
}
